import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  inject,
  input,
  viewChild,
  signal,
} from '@angular/core';
import { translation } from '@cue/translations';
import { BehaviorSubject, forkJoin, never, of, Subject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { getIndexFromDate } from '@assist/shared/data';
import { DataService } from '@assist/shared/data';
import { NgStyle } from '@angular/common';

export interface AttendeeDropdownItem {
  text: string;
  value: string;
  initials: string;
  color: string;
  isGroup: boolean;
}

@Component({
  selector: 'assist-attendees-dropdown-item',
  templateUrl: './attendees-dropdown-item.component.html',
  styleUrls: ['./attendees-dropdown-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle],
})
export class AttendeesDropdownItemComponent implements OnDestroy, OnChanges, OnInit {
  private dataService = inject(DataService);
  preferAttendeeCalendar = input(false);
  iCalUid = input('');
  email = input('');
  name = input('N A');
  from = input<Date>();
  to = input<Date>();
  lowerBound = input<Date>();
  currentEventLowerBound = input<Date>();
  currentEventUpperBound = input<Date>();
  ignoreTimes = input<boolean>();
  upperBound = input<Date>();
  displayInfo = input(true);
  canvas = viewChild<ElementRef<HTMLCanvasElement>>('canvas');
  request$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  lowerBoundIndex = 0;
  upperBoundIndex = 0;
  color = signal('orange');
  initials = 'NA';

  ngOnInit() {
    if (!this.ignoreTimes()) {
      this.request$
        .pipe(
          filter((x) => x != null),
          switchMap(([email, lowerBound, upperBound, from, to, currentEventLowerBound, currentEventUpperBound]) => {
            if (this.ignoreTimes()) return never();
            return forkJoin([
              this.dataService.getScheduleForAttendee(email, lowerBound, upperBound),
              of([email, lowerBound, upperBound, from, to, currentEventLowerBound, currentEventUpperBound]),
              this.email() != null && this.iCalUid() != null && this.iCalUid() != ''
                ? this.dataService.getEventBounds(this.email(), this.iCalUid())
                : of(null),
            ]);
          })
        )
        .subscribe(([resp, data, eventBounds]) => {
          this.lowerBoundIndex = getIndexFromDate(data[1], data[3]);
          this.upperBoundIndex = getIndexFromDate(data[1], data[4]) - 1;

          const eventLowerBound = eventBounds != null ? new Date(eventBounds.start) : data[5];
          const eventUpperBound = eventBounds != null ? new Date(eventBounds.end) : data[6];

          const currentEventLowerBoundIndex = getIndexFromDate(data[1], eventLowerBound);
          const currentEventUpperBoundIndex = getIndexFromDate(data[1], eventUpperBound) - 1;

          this.availability = resp.split('');

          const ctx = this.canvas().nativeElement.getContext('2d');
          const segmentWidth = this.canvas().nativeElement.width / this.availability.length;

          if (segmentWidth < 1) {
            ctx.fillStyle = 'orange';
            ctx.strokeStyle = 'orange';
            ctx.fillRect(0, 0, this.canvas().nativeElement.width, this.canvas().nativeElement.height);
          } else {
            this.availability.forEach((v, index) => {
              let color = '';
              const value = this.availability[index];
              const isOut = index < this.lowerBoundIndex || index > this.upperBoundIndex;

              const isCurrentEvent = index >= currentEventLowerBoundIndex && index <= currentEventUpperBoundIndex;
              if (isCurrentEvent) {
                color = `rgba(39, 174, 96, ${isOut ? 0.1 : 1})`;
              } else if (value == 1) {
                color = `rgba(255, 165, 0, ${isOut ? 0.1 : 1})`;
              } else if (value == 0) {
                color = `rgba(0, 128, 0, ${isOut ? 0.1 : 1})`;
              } else {
                color = `rgba(255, 0, 0, ${isOut ? 0.1 : 1})`;
              }

              // calculate new color based on string

              ctx.fillStyle = 'white';
              ctx.strokeStyle = 'white';
              ctx.fillRect(
                Math.floor(index * segmentWidth),
                0,
                Math.ceil(segmentWidth),
                this.canvas().nativeElement.height
              );

              ctx.fillStyle = color;
              ctx.strokeStyle = color;
              ctx.fillRect(
                Math.floor(index * segmentWidth),
                0,
                Math.ceil(segmentWidth),
                this.canvas().nativeElement.height
              );
            });
          }

          this.color.set(
            this.availability.find(
              (x, i) => i >= this.lowerBoundIndex && i <= this.upperBoundIndex && x != '0' && x != '1'
            ) == null
              ? 'green'
              : 'red'
          );
        });
    }
  }

  translation = translation;

  availability: any[] = [];
  ngOnDestroy() {
    // keep this
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['name']) {
      this.initials =
        changes['name'].currentValue.trim().split(' ').length > 1
          ? changes['name'].currentValue.trim().split(' ')[0][0] + changes['name'].currentValue.trim().split(' ')[1][0]
          : '';
    }
    if (changes['lowerBound'] || changes['upperBound']) {
      const lowerBound = changes['lowerBound']?.currentValue ?? this.lowerBound();
      const upperBound = changes['upperBound']?.currentValue ?? this.upperBound();
      const currentEventLowerBound = changes['currentEventLowerBound']?.currentValue ?? this.currentEventLowerBound();
      const currentEventUpperBound = changes['currentEventUpperBound']?.currentValue ?? this.currentEventUpperBound();

      const from = changes['from']?.currentValue ?? this.from();
      const to = changes['to']?.currentValue ?? this.to();
      this.request$.next([
        this.email(),
        lowerBound,
        upperBound,
        from,
        to,
        currentEventLowerBound,
        currentEventUpperBound,
      ]);
    }
  }
}
